import { useQuery } from 'react-query'
import { useOrganization } from 'hooks/context/organization-context'
import { isEqual } from 'lodash'
import memoizeOne from 'memoize-one'
import queryString from 'query-string'

import { getResourceName, getDelegationName, hasMoreDelegations } from 'lib/resources/transformations'
import { apiFetch, omitEmpty } from 'lib/action-helpers'
import { resourcePaths as paths, RESOURCES_LIST } from './index'
import { useDelegationsQuery } from '../delegations/index'

const transform = (resources, delegations) => {
  if (!resources) return undefined

  const moreDelegations = hasMoreDelegations(Object.values(resources))
  return Object.keys(resources)?.reduce((acc, resourceId) => {
    const resource = resources[resourceId]
    acc[resourceId] = {
      ...resource,
      title: getResourceName(resource),
      ...(moreDelegations ? { delegationName: getDelegationName(delegations, resource) } : [])
    }

    return acc
  }, {})
}

const memoizedResponse = memoizeOne(transform, isEqual)

const getResources = async (account, { type, parent, page, limit, next } = {}) => {
  const query = queryString.stringify(omitEmpty({ type, parent, page, limit, next }))
  const data = await apiFetch(paths.filter(paths.resources(account), query))

  return data?.data?.reduce((acc, item) => {
    acc[item.id] = item
    return acc
  }, {})
}

export const useResourcesQuery = (query) => {
  const { account } = useOrganization()

  const { data: delegationsData } = useDelegationsQuery()

  const queryOpts = useQuery(
    [...RESOURCES_LIST, account?.id, query && query],
    () => getResources(account?.id, query),
    {
      enabled: !!account?.id,
      refetchOnWindowFocus: false,
      notifyOnChangeProps: 'tracked',
      staleTime: 30 * 60 * 1000
    }
  )

  const data = memoizedResponse(queryOpts?.data, delegationsData)

  return {
    ...queryOpts,
    data
  }
}
